import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { base_url } from '../../../Utils/baseUrl';
import { config } from '../../../Utils/axiosconfig';
import commonFunction from '../../../Utils/commonFunction';
import './NoticeModal.css';
import { useTranslation } from 'react-i18next';

const SchoolStatusModal = ({ show, onClose, onConfirm, message, showSchoolStatusModalData }) => {

    const { t } = useTranslation();
    const [schools, setSchools] = useState([]);
    const [classes, setClasses] = useState([]);
    const [selectedSchoolId, setSelectedSchoolId] = useState('');
    const [selectedClassId, setSelectedClassId] = useState('');
    const [loadingSchools, setLoadingSchools] = useState(false);
    const [loadingClasses, setLoadingClasses] = useState(false);
    const [errors, setErrors] = useState({
        school: '',
        class: ''
    });
    const [isSuccess, setIsSuccess] = useState(false);

    useEffect(() => {
        if (!show) return undefined;

        let cancelled = false;
        setSelectedSchoolId('');
        setSelectedClassId('');
        setClasses([]);

        const loadSchools = async () => {
            setLoadingSchools(true);
            try {
                const response = await commonFunction.getSchools();
                if (!cancelled) {
                    setSchools(response?.data?.data?.school || []);
                }
            } catch (error) {
                console.error('Unable to load schools:', error);
                if (!cancelled) setSchools([]);
            } finally {
                if (!cancelled) setLoadingSchools(false);
            }
        };

        loadSchools();
        return () => {
            cancelled = true;
        };
    }, [show]);

    useEffect(() => {
        if (!show || !selectedSchoolId) {
            setClasses([]);
            setLoadingClasses(false);
            return undefined;
        }

        let cancelled = false;
        const loadClasses = async () => {
            setLoadingClasses(true);
            try {
                const response = await axios.get(
                    `${base_url}getSchoolClassByschoolId/${selectedSchoolId}`,
                    config
                );
                if (!cancelled) {
                    setClasses(response?.data?.data?.schoolclass || []);
                }
            } catch (error) {
                console.error('Unable to load school classes:', error);
                if (!cancelled) setClasses([]);
            } finally {
                if (!cancelled) setLoadingClasses(false);
            }
        };

        loadClasses();
        return () => {
            cancelled = true;
        };
    }, [show, selectedSchoolId]);

    if (!show) return null;

    const handleConfirm = async () => {
        const newErrors = {
            school: '',
            class: ''
        };

        if (!selectedSchoolId) {
            newErrors.school =
                t("common.SelectSchoolRequired") || 'Please select a school';
        }

        if (!selectedClassId) {
            newErrors.class =
                t("common.SelectClassRequired") || 'Please select a class';
        }

        setErrors(newErrors);

        if (newErrors.school || newErrors.class) {
            return;
        }

        const values = {
            schoolId: selectedSchoolId,
            classId: selectedClassId
        };

        const childId = showSchoolStatusModalData?.id;

        try {
            const response = await commonFunction.updateChildSchool(
                childId,
                values
            );

            if (response?.status === true) {
                setIsSuccess(true);
            } else {
                setErrors({
                    school: '',
                    class: response?.message || 'Something went wrong.'
                });
            }
        } catch (error) {
            console.error('Unable to update school/class:', error);

            setErrors({
                school: '',
                class: 'Der opstod en fejl. Prøv venligst igen.'
            });
        }
    };

    const handleSuccessClose = () => {
        const childId = showSchoolStatusModalData?.id;
        if (onConfirm) {
            onConfirm({
                childId,
                schoolId: selectedSchoolId,
                classId: selectedClassId
            });
        }
    };

    return (
        <div className="modal-overlay school-status-modal-overlay">
            <div className="modal-container school-status-modal-container" role="dialog" aria-modal="true">
                <div className="school-status-modal-card">
                    <div className="school-status-modal-header">
                        <div className="school-status-icon" aria-hidden="true">
                            !
                        </div>
                        <span className="school-status-badge">HANDLING PÅKRÆVET</span>
                        <button className="modal-close" onClick={onClose} aria-label="Luk">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </button>
                    </div>

                    <div className="school-status-modal-title">Barnets skole er ikke længere aktiv</div>

                    <div className="school-status-message-box">
                        <div className="school-status-message-icon" aria-hidden="true">
                            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="12" y1="8" x2="12" y2="12"></line>
                                <line x1="12" y1="16" x2="12.01" y2="16"></line>
                            </svg>
                        </div>
                        <div className="school-status-message-copy">
                            <span dangerouslySetInnerHTML={{ __html: message }} />
                        </div>
                    </div>

                    <div className="school-status-child-summary">
                        <div className="school-status-child-avatar" aria-hidden="true">
                            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                            </svg>
                        </div>
                        <div className="school-status-child-text">
                            <div className="school-status-label">Barn: {showSchoolStatusModalData?.name} ({showSchoolStatusModalData?.SchoolClass?.className})</div>
                            <div className="school-status-subtext">Nuværende skole: {showSchoolStatusModalData?.School?.schoolName}</div>
                        </div>
                    </div>

                    <div className="school-status-form-section">
                        <label className="school-status-form-label" htmlFor="school-status-school">
                            {t("common.SelectedSchool") || "Vælg skole"}
                        </label>
                        <div className="school-status-school-fields">
                            <div className="school-status-field">
                                <select
                                    id="school-status-school"
                                    value={selectedSchoolId}
                                    onChange={(event) => {
                                        setSelectedSchoolId(event.target.value);
                                        setSelectedClassId('');
                                        setClasses([]);
                                        setErrors((prev) => ({
                                            ...prev,
                                            school: '',
                                            class: ''
                                        }));
                                    }}
                                    className={errors.school ? 'input-error' : ''}
                                >
                                    <option value="" disabled>
                                        {loadingSchools ? 'Indlæser skoler...' : 'Vælg en skole'}
                                    </option>
                                    {schools.map((school) => (
                                        <option key={school.id} value={school.id}>
                                            {school.schoolName}
                                        </option>
                                    ))}
                                </select>
                                {errors.school && (
                                    <div className="field-error">
                                        {errors.school}
                                    </div>
                                )}
                            </div>
                            <div className="school-status-field">
                                <select
                                    id="school-status-class"
                                    value={selectedClassId}
                                    onChange={(event) => {
                                        setSelectedClassId(event.target.value);
                                        setErrors((prev) => ({
                                            ...prev,
                                            class: ''
                                        }));
                                    }}
                                    disabled={!selectedSchoolId || loadingClasses}
                                    className={errors.class ? 'input-error' : ''}
                                >
                                    <option value="" disabled>
                                        {!selectedSchoolId
                                            ? 'Vælg først en skole'
                                            : loadingClasses
                                                ? 'Indlæser klasser...'
                                                : 'Vælg en klasse'}
                                    </option>
                                    {classes.map((schoolClass) => (
                                        <option key={schoolClass.id} value={schoolClass.id}>
                                            {schoolClass.className}
                                        </option>
                                    ))}
                                    {selectedSchoolId && !loadingClasses && classes.length === 0 && (
                                        <option value="" disabled>{t("common.Noclassesavailable") || "Ingen klasser tilgængelige"}</option>
                                    )}
                                </select>
                                {errors.class && (
                                    <div className="field-error">
                                        {errors.class}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="school-status-actions">
                        <button className="school-status-secondary-btn" type='button' onClick={onClose}>
                            {t("common.cancel") || "ANNULLER"}
                        </button>
                        <button className="school-status-primary-btn" type='button' onClick={handleConfirm}>
                            {t("common.updateSchool") || "OPDATER SKOLE"}
                        </button>
                    </div>

                    {isSuccess && (
                        <div className="school-status-success-backdrop">
                            <div className="school-status-success-popup" role="dialog" aria-modal="true">
                                <button
                                    type="button"
                                    className="school-status-success-close"
                                    onClick={handleSuccessClose}
                                    aria-label="Luk"
                                >
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="18" y1="6" x2="6" y2="18"></line>
                                        <line x1="6" y1="6" x2="18" y2="18"></line>
                                    </svg>
                                </button>

                                <div className="school-status-success-graphic">
                                    <svg viewBox="0 0 100 100" width="86" height="86" aria-hidden="true">
                                        <line x1="28.8" y1="28.8" x2="23.1" y2="23.1" stroke="#86efac" strokeWidth="3.2" strokeLinecap="round" />
                                        <line x1="21.0" y1="42.2" x2="13.3" y2="40.2" stroke="#86efac" strokeWidth="3.2" strokeLinecap="round" />
                                        <line x1="22.8" y1="62.7" x2="15.6" y2="66.1" stroke="#86efac" strokeWidth="3.2" strokeLinecap="round" />
                                        <line x1="71.2" y1="28.8" x2="76.9" y2="23.1" stroke="#86efac" strokeWidth="3.2" strokeLinecap="round" />
                                        <line x1="79.0" y1="42.2" x2="86.7" y2="40.2" stroke="#86efac" strokeWidth="3.2" strokeLinecap="round" />
                                        <line x1="77.2" y1="62.7" x2="84.4" y2="66.1" stroke="#86efac" strokeWidth="3.2" strokeLinecap="round" />
                                        <circle cx="50" cy="50" r="25" fill="#dcfce7" />
                                        <circle cx="50" cy="50" r="14.5" fill="#22c55e" />
                                        <path d="M43.8 50.2L48 54.4L56.2 46.2" fill="none" stroke="#ffffff" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </div>

                                <h3 className="school-status-success-title">Skole opdateret</h3>

                                <div className="school-status-success-message">
                                    <p>Barnets skole er blevet opdateret.</p>
                                    <p>Du kan nu fortsætte med at bestille mad.</p>
                                </div>

                                <button
                                    type="button"
                                    className="school-status-success-btn"
                                    onClick={handleSuccessClose}
                                >
                                    OK
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SchoolStatusModal;
